import React, { useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined'
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined'
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined'
import SnapshotButton from './SnapshotButton'
import api from 'utils/api'
import { getApiErrorMessage } from 'utils/apiErrors'
import { useReadOnly } from 'utils/lifecycle'

export default function ClaimFeedback({ claim, onSubmitted }) {
  const readOnly = useReadOnly()
  const [open, setOpen] = useState(false)
  const [choice, setChoice] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [submitted, setSubmitted] = useState(null)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const feedback = submitted || claim.feedback
  const canSubmit = !readOnly && feedback?.eligible && !feedback?.outcome
  const tooltipProps = {
    enterDelay: 500,
    leaveDelay: 50,
    open: tooltipOpen,
    onOpen: () => setTooltipOpen(true),
    onClose: () => setTooltipOpen(false),
  }
  const submit = async () => {
    if (!canSubmit || !choice || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.post(`/user/claims/${claim.snapshotId || claim.claimId}/feedback`, { outcome: choice })
      setSubmitted(result)
      setOpen(false)
      setChoice(null)
      await onSubmitted?.()
    } catch (e) {
      setError(getApiErrorMessage(e, 'Feedback could not be submitted. Try again.'))
    } finally {
      setBusy(false)
    }
  }
  let feedbackAction = null
  if (feedback?.outcome) {
    const worked = feedback.outcome === 'WORKED'
    const label = `Your feedback: ${worked ? 'Worked' : 'Didn’t work'}`
    feedbackAction = (
      <Tooltip title={label} {...tooltipProps}>
        <Box
          aria-label={label}
          component="span"
          onBlur={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(true)}
          role="img"
          sx={{ alignItems: 'center', color: 'text.secondary', display: 'inline-flex', height: 36, justifyContent: 'center', width: 36 }}
          tabIndex={0}
        >
          {worked ? <ThumbUpOutlined fontSize="small" /> : <ThumbDownOutlined fontSize="small" />}
        </Box>
      </Tooltip>
    )
  } else if (feedback && (feedback.eligible || readOnly)) {
    feedbackAction = canSubmit ? (
      <Tooltip title="Provide feedback" {...tooltipProps}>
        <IconButton
          aria-label="Provide feedback"
          onBlur={() => setTooltipOpen(false)}
          onClick={() => {
            setError(null)
            setChoice(null)
            setTooltipOpen(false)
            setOpen(true)
          }}
          onFocus={() => setTooltipOpen(true)}
          size="small"
          sx={{ height: 36, width: 36 }}
        >
          <FeedbackOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : (
      <Tooltip title="Provide feedback unavailable while your account is read-only." {...tooltipProps}>
        <span
          aria-label="Provide feedback unavailable"
          onBlur={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(true)}
          role="group"
          tabIndex={0}
        >
          <IconButton aria-label="Provide feedback" disabled size="small" sx={{ height: 36, width: 36 }}>
            <FeedbackOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    )
  }
  return <>
    <SnapshotButton claim={claim} />
    {feedbackAction}
    <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} fullWidth maxWidth="xs">
      <DialogTitle>Provide feedback</DialogTitle>
      <DialogContent>
        <Typography>Did this offer work for you? Your feedback cannot be changed after submitting.</Typography>
        <Stack direction="row" spacing={1} sx={{ my: 2 }}>
          <Button startIcon={<ThumbUpOutlined />} variant={choice === 'WORKED' ? 'contained' : 'outlined'} aria-pressed={choice === 'WORKED'} disabled={!canSubmit || busy} onClick={() => setChoice('WORKED')}>Worked</Button>
          <Button startIcon={<ThumbDownOutlined />} variant={choice === 'DID_NOT_WORK' ? 'contained' : 'outlined'} aria-pressed={choice === 'DID_NOT_WORK'} disabled={!canSubmit || busy} onClick={() => setChoice('DID_NOT_WORK')}>Didn’t work</Button>
        </Stack>
        {readOnly && <Alert severity="info">Feedback is unavailable while your account is read-only.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
        <Button disabled={!canSubmit || !choice || busy} onClick={submit}>Submit feedback</Button>
      </DialogActions>
    </Dialog>
  </>
}
